import { computed, type MaybeRefOrGetter, toValue } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { useAbort } from '@/modules/common/composables/use-abort.ts';
import { wrapSpaces } from '@/modules/common/utils/wrap-spaces.util.ts';
import { DocumentQueryOptions } from '@/modules/document/query-options/document.query-options.ts';

export function useDocumentMask(text: MaybeRefOrGetter<string>, mask: MaybeRefOrGetter<string | null>) {
  const { signal } = useAbort();

  const hasMask = computed(() => !!toValue(mask));

  const { data: documentTypes } = useQuery(DocumentQueryOptions.getTypes(signal.value, hasMask));

  const masks = computed(
    () => documentTypes.value?.map((type) => type.mask).filter((mask): mask is string => !!mask) || [],
  );

  const hasMasks = computed(() => !!masks.value.length);

  const { data: masksMap } = useQuery({ ...DocumentQueryOptions.getMask(masks), enabled: hasMasks });

  return computed(() => {
    const maskValue = toValue(mask);
    const params = masksMap.value?.find((maskMap) => maskMap.mask === maskValue)?.params;

    if (!params) {
      return null;
    }

    const textValue = toValue(text);

    const pairs = params.flatMap((param, index, array) => [
      textValue.slice(index === 0 ? 0 : array[index - 1].end, param.start) + ' ',
      wrapSpaces(textValue.slice(param.start, param.end)),
    ]);

    const end = textValue.slice(params[params.length - 1].end, textValue.length);

    return [...pairs, end];
  });
}
